import { useEffect, useMemo, useState } from 'react';
import { useResource } from '../../hooks/useResource';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import type { Inbox } from '../../lib/types';
import { InboxPicker } from './InboxPicker';
import { sx } from './styles';

const TIMEZONES = ['Asia/Jerusalem', 'UTC', 'US/Eastern', 'Europe/London'];

type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
const DAY_ORDER: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

interface DaySchedule {
  enabled: boolean;
  open: string; // HH:MM
  close: string;
}

interface Holiday {
  date: string; // YYYY-MM-DD
  label?: string;
}

interface BusinessHours {
  tz: string;
  schedule: Record<DayKey, DaySchedule>;
  holidays: Holiday[];
  offlineMessage: string;
}

// Allow extra fields without breaking the existing Inbox shape.
interface InboxWithHours extends Inbox {
  businessHours?: Partial<BusinessHours>;
}

const DEFAULT_DAY: DaySchedule = { enabled: true, open: '09:00', close: '17:00' };

function defaultBusinessHours(): BusinessHours {
  const schedule = DAY_ORDER.reduce((acc, d) => {
    acc[d] = { ...DEFAULT_DAY, enabled: d !== 'sat' && d !== 'sun' };
    return acc;
  }, {} as Record<DayKey, DaySchedule>);
  return {
    tz: 'US/Eastern',
    schedule,
    holidays: [],
    offlineMessage: '',
  };
}

export function WorkingHoursPanel({ canEdit }: { canEdit: boolean }) {
  const { items: inboxes, loading } = useResource<InboxWithHours>('inboxes');
  const [inboxId, setInboxId] = useState<string | null>(null);
  const [draft, setDraft] = useState<BusinessHours>(defaultBusinessHours());
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [newHoliday, setNewHoliday] = useState<{ date: string; label: string }>({ date: '', label: '' });

  const selected = useMemo(() => inboxes.find((i) => i.id === inboxId) || null, [inboxes, inboxId]);

  useEffect(() => {
    if (!inboxId && inboxes.length > 0) setInboxId(inboxes[0].id);
  }, [inboxes, inboxId]);

  useEffect(() => {
    if (!selected) return;
    const bh = selected.businessHours;
    const base = defaultBusinessHours();
    setDraft({
      tz: bh?.tz ?? base.tz,
      schedule: { ...base.schedule, ...(bh?.schedule as Record<DayKey, DaySchedule> | undefined) },
      holidays: Array.isArray(bh?.holidays) ? (bh!.holidays as Holiday[]) : [],
      offlineMessage: bh?.offlineMessage ?? '',
    });
  }, [selected]);

  async function save() {
    if (!inboxId) return;
    setSaving(true);
    try {
      await api.patch('inboxes', inboxId, { businessHours: draft });
      setSavedAt(Date.now());
    } catch (e) {
      alert(`Save failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setSaving(false);
    }
  }

  function updateDay(day: DayKey, patch: Partial<DaySchedule>) {
    setDraft((d) => ({
      ...d,
      schedule: { ...d.schedule, [day]: { ...d.schedule[day], ...patch } },
    }));
  }

  function addHoliday() {
    if (!newHoliday.date) return;
    if (draft.holidays.some((h) => h.date === newHoliday.date)) return;
    setDraft((d) => ({
      ...d,
      holidays: [...d.holidays, { date: newHoliday.date, label: newHoliday.label || undefined }].sort(
        (a, b) => a.date.localeCompare(b.date)
      ),
    }));
    setNewHoliday({ date: '', label: '' });
  }

  function removeHoliday(date: string) {
    setDraft((d) => ({ ...d, holidays: d.holidays.filter((h) => h.date !== date) }));
  }

  return (
    <section>
      <h2 style={{ margin: 0, fontSize: 18, marginBottom: 12 }}>{t('settings.workingHours.title')}</h2>
      {loading && <div>{t('common.loading')}</div>}
      <InboxPicker inboxes={inboxes} value={inboxId} onChange={setInboxId} />

      {selected && (
        <>
          <div style={sx.card}>
            <label style={sx.label}>{t('settings.workingHours.timezone')}</label>
            <select
              value={draft.tz}
              onChange={(e) => setDraft((d) => ({ ...d, tz: e.target.value }))}
              style={{ ...sx.input, maxWidth: 320 }}
              disabled={!canEdit}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>

          <div style={sx.card}>
            <label style={sx.label}>{t('settings.workingHours.schedule')}</label>
            <table style={sx.table}>
              <tbody>
                {DAY_ORDER.map((d) => {
                  const day = draft.schedule[d];
                  return (
                    <tr key={d} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ ...sx.td, width: 140 }}>{t(`settings.workingHours.day.${d}`)}</td>
                      <td style={{ ...sx.td, width: 100 }}>
                        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="checkbox"
                            checked={day.enabled}
                            disabled={!canEdit}
                            onChange={(e) => updateDay(d, { enabled: e.target.checked })}
                          />
                          {t('common.enabled')}
                        </label>
                      </td>
                      <td style={sx.td}>
                        <input
                          type="time"
                          value={day.open}
                          onChange={(e) => updateDay(d, { open: e.target.value })}
                          disabled={!canEdit || !day.enabled}
                          style={{ ...sx.input, maxWidth: 120 }}
                        />
                      </td>
                      <td style={{ ...sx.td, textAlign: 'center', color: '#6b7280' }}>—</td>
                      <td style={sx.td}>
                        <input
                          type="time"
                          value={day.close}
                          onChange={(e) => updateDay(d, { close: e.target.value })}
                          disabled={!canEdit || !day.enabled}
                          style={{ ...sx.input, maxWidth: 120 }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={sx.card}>
            <label style={sx.label}>{t('settings.workingHours.holidays')}</label>
            <table style={sx.table}>
              <tbody>
                {draft.holidays.map((h) => (
                  <tr key={h.date}>
                    <td style={{ ...sx.td, width: 140 }}>{h.date}</td>
                    <td style={sx.td}>{h.label || '—'}</td>
                    <td style={{ ...sx.td, width: 80 }}>
                      {canEdit && (
                        <button onClick={() => removeHoliday(h.date)} style={sx.btnDanger}>
                          {t('common.remove')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {canEdit && (
              <div style={{ ...sx.row, marginTop: 8 }}>
                <input
                  type="date"
                  value={newHoliday.date}
                  onChange={(e) => setNewHoliday((h) => ({ ...h, date: e.target.value }))}
                  style={{ ...sx.input, maxWidth: 160 }}
                />
                <input
                  type="text"
                  placeholder={t('common.description')}
                  value={newHoliday.label}
                  onChange={(e) => setNewHoliday((h) => ({ ...h, label: e.target.value }))}
                  style={{ ...sx.input, maxWidth: 240 }}
                />
                <button onClick={addHoliday} style={sx.btnSecondary}>
                  {t('settings.workingHours.addHoliday')}
                </button>
              </div>
            )}
          </div>

          <div style={sx.card}>
            <label style={sx.label}>{t('settings.workingHours.offlineMessage')}</label>
            <textarea
              value={draft.offlineMessage}
              onChange={(e) => setDraft((d) => ({ ...d, offlineMessage: e.target.value }))}
              disabled={!canEdit}
              rows={3}
              style={{ ...sx.input, fontFamily: 'inherit' }}
            />
          </div>

          {canEdit && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={save} disabled={saving} style={sx.btnPrimary}>
                {saving ? t('common.loading') : t('common.save')}
              </button>
              {savedAt && <span style={sx.hint}>{new Date(savedAt).toLocaleTimeString()}</span>}
            </div>
          )}
        </>
      )}
    </section>
  );
}
